/**
 * Risk Profile Extraction Review Component
 * TASK-179: Investment Risk Profile Workflow - AI Extraction Review
 *
 * Displays AI-extracted risk profile data from signed PDFs with confidence scores
 * Allows users to review, edit, and accept/reject extracted data before final submission
 */

import React, { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  Edit3,
  Save,
  Brain,
  Info
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';

import { ConfidenceBadge, getFieldConfidence } from '../kyc/ConfidenceBadge';
import type { CompleteRiskProfile } from './schemas/riskProfileSchema';
import type { RiskProfileExtractionResponse } from '../../services/riskProfileService';
import riskProfileService from '../../services/riskProfileService';

interface RiskProfileExtractionReviewProps {
  /** Extraction result from AI processing */
  extractionData: RiskProfileExtractionResponse;
  /** Client information */
  clientId: string;
  clientName: string;
  /** Callback when data is accepted and saved */
  onAccept: (data: Partial<CompleteRiskProfile>) => void;
  /** Callback when extraction is rejected */
  onReject: () => void;
  /** Callback to close the review modal */
  onClose: () => void;
}

export const RiskProfileExtractionReview: React.FC<RiskProfileExtractionReviewProps> = ({
  extractionData,
  clientId,
  clientName,
  onAccept,
  onReject,
  onClose
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form with extracted data
  const methods = useForm<Partial<CompleteRiskProfile>>({
    defaultValues: extractionData.extracted_data,
    mode: 'onChange'
  });

  const { handleSubmit, formState: { isDirty } } = methods;

  // Handle accepting the extracted data
  const handleAcceptExtraction = async () => {
    try {
      setIsSaving(true);

      // Save the extracted data to the database
      const dataToSave = methods.getValues();
      await riskProfileService.save(clientId, dataToSave);

      // Mark as completed (uploaded PDF is already signed)
      await riskProfileService.complete(clientId);

      toast.success('Risk profile completed successfully', {
        description: 'Signed PDF data has been extracted and saved'
      });

      onAccept(dataToSave);
    } catch (error) {
      console.error('Error accepting extraction:', error);
      toast.error('Failed to save extracted data');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle saving edited data
  const handleSaveEdits = async (data: Partial<CompleteRiskProfile>) => {
    try {
      setIsSaving(true);
      await riskProfileService.save(clientId, data);

      toast.success('Changes saved successfully');
      setIsEditing(false);
      onAccept(data);
    } catch (error) {
      console.error('Error saving edits:', error);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle rejecting the extraction
  const handleRejectExtraction = () => {
    toast.info('Extraction rejected', {
      description: 'You can upload a different document or fill the form manually'
    });
    onReject();
  };

  // Get confidence for a specific field
  const getConfidence = (fieldPath: string): number | null => {
    return getFieldConfidence(extractionData.confidence_scores, fieldPath);
  };

  // Render field with confidence badge
  const renderFieldWithConfidence = (
    label: string,
    value: any,
    fieldPath: string,
    formatFn?: (val: any) => string
  ) => {
    const confidence = getConfidence(fieldPath);
    const displayValue = formatFn ? formatFn(value) : String(value || '-');

    return (
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <div className="flex-1">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <div className="mt-1 text-sm text-gray-900">{displayValue}</div>
        </div>
        {confidence !== null && (
          <ConfidenceBadge
            confidence={confidence}
            fieldName={label}
            size="sm"
            className="ml-4"
          />
        )}
      </div>
    );
  };

  // Determine overall extraction quality
  const getExtractionQuality = () => {
    const confidence = extractionData.overall_confidence;
    if (confidence >= 0.90) {
      return {
        label: 'Excellent Extraction',
        color: 'text-green-700',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        icon: CheckCircle
      };
    } else if (confidence >= 0.75) {
      return {
        label: 'Good Extraction',
        color: 'text-yellow-700',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        icon: AlertTriangle
      };
    } else {
      return {
        label: 'Partial Extraction',
        color: 'text-orange-700',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        icon: AlertTriangle
      };
    }
  };

  const quality = getExtractionQuality();
  const QualityIcon = quality.icon;
  const extracted = extractionData.extracted_data;

  return (
    <Dialog.Root open onOpenChange={() => !isSaving && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-2xl z-50 w-full max-w-4xl max-h-[90vh] overflow-y-auto">

          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
            <div className="flex items-center justify-between">
              <div>
                <Dialog.Title className="text-xl font-semibold text-gray-900">
                  Review Extracted Risk Profile Data
                </Dialog.Title>
                <Dialog.Description className="mt-1 text-sm text-gray-600">
                  Client: {clientName}
                </Dialog.Description>
              </div>
              <button
                onClick={onClose}
                disabled={isSaving}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Extraction Quality Banner */}
            <div className={`mt-4 flex items-center gap-3 p-3 rounded-lg border ${quality.bgColor} ${quality.borderColor}`}>
              <QualityIcon className={`w-5 h-5 ${quality.color}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold ${quality.color}`}>{quality.label}</span>
                  <span className="text-sm text-gray-600">
                    {Math.round(extractionData.overall_confidence * 100)}% overall confidence
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-600">
                  {extractionData.extraction_metadata.validation_passed
                    ? 'All required fields were extracted successfully'
                    : 'Some fields may require manual review and editing'}
                </p>
              </div>
            </div>

            {/* Metadata */}
            <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Brain className="w-3.5 h-3.5" />
                <span>Processor: {extractionData.extraction_metadata.processor_used}</span>
              </div>
              <div className="flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" />
                <span>Extracted: {new Date(extractionData.extraction_metadata.extracted_at).toLocaleString()}</span>
              </div>
              {extractionData.extraction_metadata.gap_filled && (
                <div className="flex items-center gap-1 text-blue-600">
                  <Info className="w-3.5 h-3.5" />
                  <span>AI Gap Filling Applied</span>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(handleSaveEdits)}>
                {/* Section 1: Personal Information */}
                {extracted.section1 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">1</div>
                      Personal Information
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      {renderFieldWithConfidence('First Name', extracted.section1?.firstName, 'section1.firstName')}
                      {renderFieldWithConfidence('Last Name', extracted.section1?.lastName, 'section1.lastName')}
                      {renderFieldWithConfidence('Date of Birth', extracted.section1?.dateOfBirth, 'section1.dateOfBirth', riskProfileService.formatSwissDate)}
                      {renderFieldWithConfidence('Email', extracted.section1?.emailPrimary, 'section1.emailPrimary')}
                      {renderFieldWithConfidence('Phone', extracted.section1?.phonePrimary, 'section1.phonePrimary')}
                      {renderFieldWithConfidence('Address', extracted.section1?.residentialAddress, 'section1.residentialAddress')}
                      {renderFieldWithConfidence('Nationality', extracted.section1?.nationalities?.[0], 'section1.nationalities')}
                    </div>
                  </div>
                )}

                {/* Section 2: Knowledge & Experience */}
                {extracted.section2 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">2</div>
                      Knowledge & Experience
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      {renderFieldWithConfidence('Education Level', extracted.section2?.educationLevel, 'section2.educationLevel')}
                      {renderFieldWithConfidence('Finance Experience (Years)', extracted.section2?.financeExperienceYears, 'section2.financeExperienceYears')}
                      {renderFieldWithConfidence('Field of Study', extracted.section2?.fieldOfStudy, 'section2.fieldOfStudy')}
                      {renderFieldWithConfidence('Used Advisory Services', extracted.section2?.usedAdvisoryServices ? 'Yes' : 'No', 'section2.usedAdvisoryServices')}
                    </div>
                  </div>
                )}

                {/* Section 3: Financial Situation */}
                {extracted.section3 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">3</div>
                      Financial Situation
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                      {/* Income */}
                      {(extracted.section3?.incomeEmploymentSalary || extracted.section3?.incomeSelfEmployment || extracted.section3?.incomeOther) && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Income</h4>
                          {renderFieldWithConfidence('Employment Salary', extracted.section3?.incomeEmploymentSalary, 'section3.incomeEmploymentSalary', riskProfileService.formatSwissCurrency)}
                          {renderFieldWithConfidence('Self Employment', extracted.section3?.incomeSelfEmployment, 'section3.incomeSelfEmployment', riskProfileService.formatSwissCurrency)}
                          {renderFieldWithConfidence('Other Income', extracted.section3?.incomeOther, 'section3.incomeOther', riskProfileService.formatSwissCurrency)}
                        </div>
                      )}

                      {/* Assets */}
                      {(extracted.section3?.assetsCashDeposits || extracted.section3?.assetsSecuritiesInvestments) && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Assets</h4>
                          {renderFieldWithConfidence('Cash & Deposits', extracted.section3?.assetsCashDeposits, 'section3.assetsCashDeposits', riskProfileService.formatSwissCurrency)}
                          {renderFieldWithConfidence('Securities & Investments', extracted.section3?.assetsSecuritiesInvestments, 'section3.assetsSecuritiesInvestments', riskProfileService.formatSwissCurrency)}
                        </div>
                      )}

                      {/* Liabilities */}
                      {(extracted.section3?.liabilitiesMortgages || extracted.section3?.liabilitiesConsumerLoans) && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Liabilities</h4>
                          {renderFieldWithConfidence('Mortgages', extracted.section3?.liabilitiesMortgages, 'section3.liabilitiesMortgages', riskProfileService.formatSwissCurrency)}
                          {renderFieldWithConfidence('Consumer Loans', extracted.section3?.liabilitiesConsumerLoans, 'section3.liabilitiesConsumerLoans', riskProfileService.formatSwissCurrency)}
                        </div>
                      )}

                      {/* Portfolio */}
                      {extracted.section3?.portfolioAmountToManage && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Portfolio</h4>
                          {renderFieldWithConfidence('Amount to Manage', extracted.section3?.portfolioAmountToManage, 'section3.portfolioAmountToManage', riskProfileService.formatSwissCurrency)}
                          {renderFieldWithConfidence('Source of Funds', extracted.section3?.portfolioSourceOfFunds, 'section3.portfolioSourceOfFunds')}
                        </div>
                      )}

                      {/* Risk Capacity */}
                      {renderFieldWithConfidence('Maximum Loss Capacity', extracted.section3?.maxLossCapacity, 'section3.maxLossCapacity')}
                    </div>
                  </div>
                )}

                {/* Section 4: Investment Objectives */}
                {extracted.section4 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">4</div>
                      Investment Objectives
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      {renderFieldWithConfidence('Time Horizon', extracted.section4.timeHorizon, 'section4.timeHorizon')}
                      {renderFieldWithConfidence('Expected Return', extracted.section4.expectedReturn, 'section4.expectedReturn')}

                      {extracted.section4.investmentPurposes && extracted.section4.investmentPurposes.length > 0 && (
                        <div className="py-2 border-b border-gray-100">
                          <span className="text-sm font-medium text-gray-700">Investment Purposes</span>
                          <div className="mt-1 flex flex-wrap gap-2">
                            {extracted.section4.investmentPurposes.map((purpose, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                {purpose}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {extracted.section4?.liquidityRegularWithdrawals && (
                        <>
                          {renderFieldWithConfidence('Withdrawal Frequency', extracted.section4?.liquidityWithdrawalFrequency, 'section4.liquidityWithdrawalFrequency')}
                          {renderFieldWithConfidence('Withdrawal Amount', extracted.section4?.liquidityWithdrawalAmount, 'section4.liquidityWithdrawalAmount', riskProfileService.formatSwissCurrency)}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Section 5: Risk Tolerance */}
                {extracted.section5 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">5</div>
                      Risk Tolerance
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      {renderFieldWithConfidence('Risk Attitude', extracted.section5.riskAttitude, 'section5.riskAttitude')}
                      {renderFieldWithConfidence('Portfolio Decline Reaction', extracted.section5.portfolioDeclineReaction, 'section5.portfolioDeclineReaction')}
                      {renderFieldWithConfidence('Max Acceptable Loss (1 Year)', extracted.section5.maxAcceptableLoss1yr, 'section5.maxAcceptableLoss1yr')}

                      {/* Investment Restrictions */}
                      {extracted.section5?.restrictionsHasRestrictions && (
                        <div className="py-2 border-b border-gray-100">
                          <span className="text-sm font-medium text-gray-700">Investment Restrictions</span>
                          <div className="mt-2 space-y-1 text-sm">
                            {extracted.section5?.restrictionsEsgCriteria && extracted.section5?.restrictionsEsgSpecify && (
                              <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span>ESG Preferences: {extracted.section5.restrictionsEsgSpecify}</span>
                              </div>
                            )}
                            {extracted.section5?.restrictionsEthicalReligious && extracted.section5?.restrictionsEthicalSpecify && (
                              <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span>Ethical Restrictions: {extracted.section5.restrictionsEthicalSpecify}</span>
                              </div>
                            )}
                            {extracted.section5?.restrictionsSectorExclusions && (
                              <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span>Sector Exclusions Specified</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </form>
            </FormProvider>
          </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
            <button
              onClick={handleRejectExtraction}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <XCircle className="w-4 h-4" />
              Reject Extraction
            </button>

            <div className="flex items-center gap-3">
              {!isEditing ? (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit Fields
                  </button>
                  <button
                    onClick={handleAcceptExtraction}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {isSaving ? 'Saving...' : 'Accept & Continue'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      methods.reset(extractionData.extracted_data);
                      setIsEditing(false);
                    }}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit(handleSaveEdits)}
                    disabled={isSaving || !isDirty}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              )}
            </div>
          </div>

        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default RiskProfileExtractionReview;
